// Ground-transport wire shapes for the operator console (feat/transport slice T5).
//
// UI MIRROR of the FROZEN seams in docs/verticals/transport.md §7, kept
// byte-compatible with the operator-bff's copy
// (`apps/operator-bff/src/modules/transport/transport.types.ts`). Deliberately a
// COPY and not an import: operator-ui does not depend on the BFF package, and the
// producer of `TripView@v1` is `apps/transport` (slice T1) — this file must not
// drift from it, but it must also not couple the bundle to a Nest app.

/**
 * Which direction the ride runs. `AIRPORT_TO_HOME` is the ride home of a
 * stay-home (LOCAL_RESIDENT) passenger — a per-passenger CAR from the MANUAL door
 * only, never a shuttle seat, no return leg; its dropoff is caller-supplied.
 */
export type TripLeg = 'AIRPORT_TO_HOTEL' | 'HOTEL_TO_AIRPORT' | 'AIRPORT_TO_HOME';

/**
 * How the trip came to exist:
 *   AUTOMATIC — transport fired it off `{tenant}.booking.confirmed` (slice T2)
 *   ON_DEMAND — the passenger asked for it from the PWA (slice T7)
 *   MANUAL    — an operator dispatched it from this card
 */
export type TripDispatchMode = 'AUTOMATIC' | 'ON_DEMAND' | 'MANUAL';

/** transport-tds §3 state machine. `FAILED_COMPENSATED` = failed AND refunded. */
export type TripStatus =
  | 'REQUESTED'
  | 'DISPATCHED'
  | 'IN_TRANSIT'
  | 'COMPLETED'
  | 'CANCELLED'
  | 'FAILED'
  | 'FAILED_COMPENSATED';

/** Vendor-supplied driver identity. Absent until the vendor assigns one. */
export interface TripDriverInfo {
  name?: string;
  vehicle?: string;
  licensePlate?: string;
  /** Vehicle colour as the vendor states it (e.g. `white`) — `transport:TripView.driver@v1`, additive. */
  vehicleColor?: string;
}

/**
 * The airline's PRICE of a vendor-written transfer amount (major units) —
 * `operator-bff:airline-money@v2` (commercial-contract P5): the BFF converts Nexa's
 * cost server-side; the browser never receives it.
 */
export interface TripPrice {
  amount: number;
  currency: string;
}

/**
 * @deprecated The wire carries a price, never a cost (commercial-contract D13) — kept
 * only as an alias for the reservations transport test desk until it adopts `TripPrice`.
 */
export type TripCost = TripPrice;

/**
 * `TripView@v1` — the only trip shape that crosses the BFF boundary.
 *
 * Carries NO raw passenger PII beyond what the sub-case roster already exposes:
 * a `passengerUrn`, never a name or a phone. The name this card renders is the
 * already-masked `fullName` off the roster row, resolved locally by `passengerUrn`
 * — no extra read, no reveal (the audited reveal lives in PassengersCard).
 * `driverInfo.name` IS a name, but it is the VENDOR's driver, not our passenger.
 */
export interface TripView {
  urn: string;
  caseUrn: string;
  subCaseUrn?: string;
  passengerUrn: string;
  groupId?: string;
  leg: TripLeg;
  dispatchMode: TripDispatchMode;
  status: TripStatus;
  providerName: string;
  driverInfo?: TripDriverInfo;
  /** Minutes until pickup as the vendor last reported it — `transport:TripView.driver@v1`, additive; absent until a driver is on the way. */
  pickupEtaMinutes?: number;
  /** ISO-8601. Set for a scheduled (return-leg) trip; absent for "now". */
  scheduledAt?: string;
  /** ISO-8601 — when the trip was created. */
  requestedAt: string;
  /**
   * What the airline pays for the ride (`operator-bff:TripView@v2`, was `cost`).
   * Absent while the vendor has written no amount; `null` when the BFF could not
   * price it (no price card) — shown as "not available", never guessed.
   */
  price?: TripPrice | null;
  /** The vendor's committed fare at dispatch, priced (M4); absent when none was sent. */
  quotedPrice?: TripPrice | null;
  /** `false` = the price card was unavailable when this view was built. */
  priced?: boolean;
  lastError?: string;
  /** WS room routing only (additive to the v1 minimum) — see the BFF's copy. */
  disruptionUrn?: string;
}

/** `operator-bff:GET /v1/cases/{caseUrn}/trips@v2`. */
export interface TripsPage {
  items: TripView[];
  /** `false` = the price card was unavailable (every trip price `null`). */
  priced?: boolean;
}

/**
 * A trip end point on the dispatch body (`dispatch@v1` optional additions) —
 * GeoJSON `[longitude, latitude]`. `address` is a COARSE label (a city / district),
 * never the street: the full home address stays behind the audited reveal in cases.
 */
export interface GeoPoint {
  coordinates: [number, number];
  address?: string;
  placeUrn?: string;
}

/** `operator-bff:POST /v1/transport/dispatch@v1` — MANUAL dispatch. */
export interface DispatchTripsInput {
  caseUrn: string;
  passengerUrns: string[];
  leg: TripLeg;
  /**
   * Caller-supplied end points. The hotel legs derive theirs upstream; an
   * `AIRPORT_TO_HOME` leg is nothing without its `dropoff` (the geocoded home
   * point) — the BFF refuses it (`400 dropoff_required`) rather than let the
   * vendor fabricate one. `airportIata` names the pickup of a home ride.
   */
  pickup?: GeoPoint;
  dropoff?: GeoPoint;
  airportIata?: string;
  /** PNR sub-case — keeps the trip attached to its PNR group. */
  subCaseUrn?: string;
  /** Parent disruption — stamps the trip so the Transport desk's
   *  disruption-grain read (`GET /v1/disruptions/:urn/trips`) finds it even
   *  when the PNR has no confirmed hotel (the desk's caseUrns fallback only
   *  covers transfer-eligible groups). */
  disruptionUrn?: string;
  /** ISO-8601; omit for "dispatch now". */
  scheduledAt?: string;
  /** Free-text operator justification — the audit trail for overriding AUTOMATIC. */
  reason: string;
}

/** 202 body of the dispatch route. */
export interface DispatchTripsResult {
  trips: TripView[];
}

/** 200 body of the cancel route. */
export interface CancelTripResult {
  trip: TripView;
}

/**
 * States from which nothing more happens. Cancel is hidden on these (brief §6.4:
 * "cancel is a no-op when already terminal" — the BFF enforces it; the UI simply
 * never offers a button that cannot do anything).
 */
export const TERMINAL_TRIP_STATUSES: readonly TripStatus[] = [
  'COMPLETED',
  'CANCELLED',
  'FAILED',
  'FAILED_COMPENSATED',
];

export function isTerminalTripStatus(status: TripStatus): boolean {
  return TERMINAL_TRIP_STATUSES.includes(status);
}

/** A trip the operator may cancel — anything not yet terminal. */
export function isCancellable(trip: TripView): boolean {
  return !isTerminalTripStatus(trip.status);
}

/**
 * A trip the operator may re-dispatch. Brief §6.12: re-dispatch targets ONLY
 * failed/expired trips — a CONFIRMED or COMPLETED ride is never disturbed, and a
 * CANCELLED one was cancelled on purpose (dispatch a fresh trip from the card's
 * own button instead of resurrecting it).
 */
export function isRedispatchable(trip: TripView): boolean {
  return trip.status === 'FAILED' || trip.status === 'FAILED_COMPENSATED';
}

/**
 * Operator-facing leg copy, as `transport` translation KEYS. This module is not
 * a component, so it holds keys and never language (same rule as
 * `routes/flights/status.ts`); the rendering component calls `t()`.
 *
 * The keys carry their NAMESPACE prefix on purpose — the consumers sit in
 * different namespaces (`pnrActions` for the PNR card and its dialogs,
 * `transport` for the desk), so `t(TRIP_LEG_LABEL_KEY[leg])` resolves the same
 * caption from any of them. Friendly label primary (frontend-conventions §18).
 */
export const TRIP_LEG_LABEL_KEY: Record<TripLeg, string> = {
  AIRPORT_TO_HOTEL: 'transport:leg.airportToHotel',
  HOTEL_TO_AIRPORT: 'transport:leg.hotelToAirport',
  AIRPORT_TO_HOME: 'transport:leg.airportToHome',
};

export const TRIP_LEGS: readonly TripLeg[] = ['AIRPORT_TO_HOTEL', 'HOTEL_TO_AIRPORT', 'AIRPORT_TO_HOME'];

export function isTripLeg(value: unknown): value is TripLeg {
  return typeof value === 'string' && (TRIP_LEGS as readonly string[]).includes(value);
}

/**
 * How each state reads to an operator, and what it looks like. The two halves
 * are deliberately different kinds of thing: `tone` is PRESENTATION and lives
 * here, the caption and the hint are LANGUAGE and live in the `transport`
 * bundle — this module only names them.
 */
export interface TripStatusPresentation {
  /** `transport` translation key for the badge caption. */
  labelKey: string;
  /** `transport` key for the one-line "what is actually happening" under the badge. */
  hintKey: string;
  tone: 'muted' | 'info' | 'warning' | 'success' | 'error' | 'neutral';
}

export const TRIP_STATUS_PRESENTATION: Record<TripStatus, TripStatusPresentation> = {
  REQUESTED: {
    labelKey: 'transport:tripStatus.requested.label',
    hintKey: 'transport:tripStatus.requested.hint',
    tone: 'muted',
  },
  DISPATCHED: {
    labelKey: 'transport:tripStatus.dispatched.label',
    hintKey: 'transport:tripStatus.dispatched.hint',
    tone: 'info',
  },
  IN_TRANSIT: {
    labelKey: 'transport:tripStatus.inTransit.label',
    hintKey: 'transport:tripStatus.inTransit.hint',
    tone: 'warning',
  },
  COMPLETED: {
    labelKey: 'transport:tripStatus.completed.label',
    hintKey: 'transport:tripStatus.completed.hint',
    tone: 'success',
  },
  CANCELLED: {
    labelKey: 'transport:tripStatus.cancelled.label',
    hintKey: 'transport:tripStatus.cancelled.hint',
    tone: 'neutral',
  },
  FAILED: {
    labelKey: 'transport:tripStatus.failed.label',
    hintKey: 'transport:tripStatus.failed.hint',
    tone: 'error',
  },
  FAILED_COMPENSATED: {
    labelKey: 'transport:tripStatus.failedCompensated.label',
    hintKey: 'transport:tripStatus.failedCompensated.hint',
    tone: 'error',
  },
};

/** How the trip came to exist, as `transport` translation keys. */
export const TRIP_DISPATCH_MODE_LABEL_KEY: Record<TripDispatchMode, string> = {
  AUTOMATIC: 'transport:dispatchMode.automatic',
  ON_DEMAND: 'transport:dispatchMode.onDemand',
  MANUAL: 'transport:dispatchMode.manual',
};
